<template>
    <div class="height100 scroll-y">
        <div v-if="addressLoading">
            <ProgressCircular class="pt-10" :color="currency.unit"/>
        </div>

        <v-container v-if="!addressLoading && !addressInfo">
            <div v-if="addressInfo === null || !addressId">
                <AddressNotFoundInfo
                    class="my-2"
                    :address="addressId"
                    :message="'Provided identifier does not exist in the blockchain!'"
                />
            </div>
        </v-container>

        <v-container v-if="!addressLoading && addressInfo" fluid class="pa-0">
            <h1>Interactions</h1>
            <v-divider/>
            <v-divider class="mb-3"/>

            <div v-if="!hasLocalClusters && !addressClustersLoading">
                <EmptyClusterInfo
                    :address="addressId"
                    :message="'This address does not form any cluster with other addresses!'"
                />
            </div>

            <div v-if="clusterNotFound">
                <AddressNotFoundInfo
                    class="my-2"
                    :address="addressId"
                    :message="'This address does not belong to the given cluster!'"
                />
            </div>

            <div class="mb-2 ml-6">
                <v-row style="max-width: 900px;" justify="center" align="center">
                    <v-col
                        class="d-flex justify-center"
                        cols="12"
                        sm="2"
                        md="1"
                        lg="1">
                        <CancelLoadingButton v-if="currentInteractionsLoading && !clusterNotFound"
                                             :color="currency.unit" @cancel-button-event="cancelRequest"/>
                    </v-col>
                    <v-col cols="12"
                           sm="10"
                           md="7"
                           lg="7">
                        <AddressClusterPicker
                            v-model="entityAddress"
                        />
                    </v-col>

                    <v-col cols="12"
                           sm="12"
                           md="4"
                           lg="4"
                           class="d-flex justify-center">

                        <v-divider vertical class="d-inline ml-n6 mt-n2"/>
                        <v-divider vertical class="d-inline mr-2 mt-n2"/>

                        <v-card loading="true" elevation="0" class="caption mt-n2" width="250px">
                            <v-row justify="center" no-gutters>

                                <v-col cols="6">
                                    <p class="mb-1">
                                        Type:
                                    </p>
                                </v-col>
                                <v-col cols="6">
                                    <p class="mb-1">
                                        <strong> {{
                                                currentLocalCluster ? currentLocalCluster.isCustom ? "custom" : "co-spent" : "#"
                                            }} </strong>
                                    </p>
                                </v-col>
                                <v-col cols="6">
                                    <p class="ma-0 pa-0">
                                        # of Addresses:
                                    </p>
                                </v-col>
                                <v-col cols="6">
                                    <p class="ma-0">
                                        <strong> {{
                                                currentLocalCluster ? currentLocalCluster.addresses : "#"
                                            }} </strong>
                                    </p>
                                </v-col>
                            </v-row>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
            <HeaderLined class="mb-1 mx-6">
                <span style="color: rgba(0, 0, 0, 0.6); font-weight: 400 ">
                    Filter
                </span>
            </HeaderLined>
            <div class="mt-2">
                <InteractionsFilter
                    :disabled="!entityAddress"
                    :currency="currency"
                    :clusterCurrent="currentCluster"
                    @search-funds-event="searchInteractions"/>
            </div>
            <HeaderLined class="mb-1 mx-6">
                <span style="color: rgba(0, 0, 0, 0.6); font-weight: 400 ">
                    Result
                </span>
            </HeaderLined>
            <InteractionsResult
                :filter="filter"
                :currency="currency"
                :addressInteractionsData="currentInteractionsData"
                :addressInteractionsLoading="currentInteractionsLoading"
                :cluster-current="currentCluster"
            />
        </v-container>
    </div>
</template>

<script>
import {mapGetters} from "vuex"
import ValidatableSearchTextField from "@/components/common/ValidateableComponents/ValidatableSearchTextField"
import AddressNotFoundInfo from '@/components/address/AddressNotFoundInfo'
import ProgressCircular from "@/components/common/ProgressCircular"
import EmptyClusterInfo from "@/components/address/EmptyClusterInfo"
import CancelLoadingButton from "@/components/common/Buttons/CancelLoadingButton";
import AddressClusterPicker from "@/components/address/AddressClusterPicker";
import HeaderLined from "@/components/common/HeaderLined";
import InteractionsFilter from "@/components/Interactions/InteractionsFilter";
import InteractionsResult from "@/components/Interactions/InteractionsResult";

export default {
    name: "addressInteractions",
    components: {
        InteractionsResult,
        InteractionsFilter,
        HeaderLined,
        AddressClusterPicker,
        CancelLoadingButton,
        ValidatableSearchTextField,
        AddressNotFoundInfo,
        ProgressCircular,
        EmptyClusterInfo
    },
    computed: {
        ...mapGetters([
            "currentInteractionsCluster", "addressInteractionsLoading", "addressInteractionsData", "addressInteractionsFilter", "addressInteractionsHistoryFallback", "currency", "addressId", "addressLoading", "addressInfo", "local_clusters", "addressClustersLoading",
        ]),
        filter()
        {
            return this.addressInteractionsHistoryFallback[this.currentCluster]
        },
        localClusters() {
            return [...this.local_clusters].map(cluster => {
                return {
                    ...cluster,
                    isLoading: !!this.addressInteractionsLoading[cluster.entityid],
                    currency: this.currency
                }
            })
        },
        currentCluster() {
            return this.currentInteractionsCluster
        },
        currentInteractionsLoading() {
            return !!this.addressInteractionsLoading[this.currentCluster]
        },
        currentInteractionsData() {
            return this.addressInteractionsData[this.currentCluster]
        },
        hasLocalClusters() {
            return this.local_clusters.length !== 0;
        },
        currentLocalCluster() {
            return this.local_clusters.filter(x => {
                return x.entityid === this.entityAddress
            })[0]
        },
        getCurrentInteractionsFallback() {
            return this.addressInteractionsHistoryFallback[this.entityAddress] ? this.addressInteractionsHistoryFallback[this.entityAddress] : undefined
        },
        getCurrentInteractionsFilter() {
            if (this.addressInteractionsFilter[this.currentCluster]) {
                return {...this.addressInteractionsFilter[this.currentCluster]}
            } else {
                return undefined
            }
        },

    },
    beforeUpdate() {
        //Update visual slider (workaround)
        this.$emit("component-loaded-event");
    },
    created() {
        if (this.$route.params.entity) {
            this.entityAddress = this.$route.params.entity
        } else if (this.currentCluster !== null) {
            this.entityAddress = this.currentCluster
        }
    },
    data() {
        return {
            entityAddress: null,
            clusterNotFound: false,
        }
    },

    methods: {
        cancelRequest() {
            this.$store.commit("ADDRESS_INTERACTIONS_LOADING_CANCEL", {entity: this.entityAddress})
            const fallback = this.addressInteractionsHistoryFallback[this.entityAddress]
            if (fallback) {
                this.$router.push({query: {...fallback}})
            } else {
                this.entityAddress = null
            }
        },

        checkCurrentClusterForExistence() {
            let clusterFound = false
            this.local_clusters.forEach(x => {
                if (x.entityid === this.entityAddress)
                    clusterFound = true
            })
            this.clusterNotFound = !clusterFound
            if(!clusterFound)
            {
                this.entityAddress = null
            }
        },

        searchInteractions(filter)
        {
            this.$store.dispatch("loadAddressInteractions", {
                currency: this.currency.unit,
                addressId: this.addressId ? this.addressId : this.$route.params.detailAddress,
                filter: filter,
                entityAddress: this.currentCluster ? this.currentCluster : this.entityAddress
            })
        }

    },
    watch: {

        $route(to, from) {
            if (to.params.entity !== from.params.entity)
            {
                if (this.$route.params.entity) {
                    this.entityAddress = this.$route.params.entity
                    this.checkCurrentClusterForExistence()
                } else
                    this.entityAddress = null
            }
        },

        "entityAddress": function (newVal, oldVal) {
            let interactionsString = "interactions"
            let indexOfLastInteractionsOccurrence = this.$route.fullPath.lastIndexOf(interactionsString)
            if (newVal) {
                this.$store.commit("ADDRESS_INTERACTIONS_SET_CURRENT", this.entityAddress)

                let newPath = `${this.$route.fullPath.slice(0, indexOfLastInteractionsOccurrence + interactionsString.length)}/${newVal}`
                if (this.$route.params.entity !== this.entityAddress) {
                    this.$router.push({path: newPath})
                }

                if (!this.currentInteractionsData && !this.currentInteractionsLoading && this.$route.query.filter) {
                    this.searchInteractions(this.$route.query.filter)
                }
            } else {
                this.$store.commit("ADDRESS_INTERACTIONS_SET_CURRENT", null)

                let newPath = `${this.$route.fullPath.slice(0, indexOfLastInteractionsOccurrence + interactionsString.length)}`
                if (this.$route.params.entity !== this.entityAddress) {
                    this.$router.push({path: newPath})
                }
            }
        },
        "local_clusters": function (newVal, oldVal) {
            if (this.entityAddress) {
                this.checkCurrentClusterForExistence()
            }
        }
    },
}
</script>

<style>
</style>